// client/components/Inventar/InventarBlockFormular.jsx
import React, { useMemo, useState } from "react";
import { INVENTAR_STATUS_OPTIONS } from "../../constants/inventarStatus";
import { INVENTAR_FELDER } from "../../constants/inventarKategorien";
import AppButton from "../ui/AppButton.jsx";
import {
  entityFormSubmitDisabledClassName,
  entityFormSubmitPrimaryClassName,
} from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import { Input as FormInput, Select, Textarea as FormTextarea } from "../forms/shared/FormAtoms.jsx";
export default function InventarBlockFormular({
  form,
  setForm,
  onSubmit,
  onCancel,
  isSaving = false,
  isEdit = false
}) {
    const [touched, setTouched] = useState(false);
  const projekttypOpts = useMemo(() => Object.keys(INVENTAR_FELDER).map(k => ({
    value: k,
    label: k
  })), []);
  const katOpts = useMemo(() => (form?.projekttyp ? Object.keys(INVENTAR_FELDER[form.projekttyp]) : []).map(k => ({
    value: k,
    label: k
  })), [form?.projekttyp]);
  const dynFields = useMemo(() => {
    const t = form?.projekttyp,
      k = form?.kategorie;
    if (!t || !k) return [];
    return INVENTAR_FELDER[t][k] || [];
  }, [form?.projekttyp, form?.kategorie]);
  const setVal = (name, value) => setForm(p => ({
    ...p,
    [name]: value
  }));
  const setDetail = (key, value) => setForm(p => ({
    ...p,
    details: {
      ...(p?.details || {}),
      [key]: value
    }
  }));
  const fieldId = name => `inventar-${name}`;
  const detailId = name => `inventar-detail-${String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "feld"}`;
  const okBase = !!(form?.bezeichnung && form?.projekttyp && form?.kategorie && form?.standort_typ);
  const handleSubmit = e => {
    setTouched(true);
    if (!okBase) {
      e.preventDefault();
      return;
    }
    onSubmit?.(e);
  };
  return <form onSubmit={handleSubmit} className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${"text-blue-50"}`}>
      <div className="md:col-span-2 mb-1">
        <h2 className={`text-xl font-semibold ${"text-blue-100/90"}`}>
          {isEdit ? "🖊 Inventar bearbeiten" : "➕ Inventar anlegen"}
        </h2>
      </div>

      {/* Stammdaten */}
      <div>
        <Label htmlFor={fieldId("projekttyp")}>Projekttyp <Req /></Label>
        <Select id={fieldId("projekttyp")} value={form.projekttyp || ""} onChange={e => {
        const v = e.target.value || "";
        setForm(p => ({
          ...p,
          projekttyp: v,
          kategorie: ""
        }));
      }} className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${"border-white/10 bg-[#0b1324] text-blue-100 focus:ring-blue-400/40 focus:border-blue-400/30"}`} disabled={isSaving} required>
          <option value="">– wählen –</option>
          {projekttypOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </div>

      <div>
        <Label htmlFor={fieldId("kategorie")}>Kategorie <Req /></Label>
        <Select id={fieldId("kategorie")} value={form.kategorie || ""} onChange={e => setVal("kategorie", e.target.value || "")} className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${"border-white/10 bg-[#0b1324] text-blue-100 focus:ring-blue-400/40 focus:border-blue-400/30"}`} disabled={!form.projekttyp || isSaving} required>
          <option value="">– wählen –</option>
          {katOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </div>

      <div className="md:col-span-2">
        <Label htmlFor={fieldId("bezeichnung")}>Bezeichnung <Req /></Label>
        <Input id={fieldId("bezeichnung")} aria-label="Inventar Bezeichnung" value={form.bezeichnung || ""} onChange={e => setVal("bezeichnung", e.target.value)} placeholder="z. B. Fluke 1664 FC | BYD HVS 10.2 | Bosch GBH 2-28" maxLength={255} disabled={isSaving} required />
      </div>

      <div>
        <Label htmlFor={fieldId("standort-typ")}>Standorttyp <Req /></Label>
        <Select id={fieldId("standort-typ")} value={form.standort_typ || "Lager"} onChange={e => setVal("standort_typ", e.target.value)} className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${"border-white/10 bg-[#0b1324] text-blue-100 focus:ring-blue-400/40 focus:border-blue-400/30"}`} disabled={isSaving} required>
          {["Lager", "Fahrzeug", "Baustelle", "Kunde"].map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
      </div>

      <div>
        <Label htmlFor={fieldId("standort-bez")}>Standort-Bezeichnung</Label>
        <Input id={fieldId("standort-bez")} aria-label="Inventar Standort-Bezeichnung" value={form.standort_bez || ""} onChange={e => setVal("standort_bez", e.target.value)} placeholder="z. B. Bus Jonas | Projekt 123 – Schulhaus" maxLength={255} disabled={isSaving} />
      </div>

      <div>
        <Label htmlFor={fieldId("status")}>Status</Label>
        <Select id={fieldId("status")} value={form.status || "verfügbar"} onChange={e => setVal("status", e.target.value || "verfügbar")} className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${"border-white/10 bg-[#0b1324] text-blue-100 focus:ring-blue-400/40 focus:border-blue-400/30"}`} disabled={isSaving}>
          {INVENTAR_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
      </div>

      <div className="md:col-span-2">
        <Label htmlFor={fieldId("notiz")}>Notiz</Label>
        <Textarea id={fieldId("notiz")} aria-label="Inventar Notiz" rows={3} value={form.notiz || ""} onChange={e => setVal("notiz", e.target.value)} placeholder="z. B. Letzte Kalibrierung 06/2025, Ladeadapter im Koffer" maxLength={4000} disabled={isSaving} />
      </div>

      {/* Kategorie-spezifisch + Hersteller/Seriennummer (unten) */}
      {form.projekttyp && form.kategorie && <div className="md:col-span-2">
          <h3 className={`text-sm font-semibold mb-2 ${"text-blue-200/80"}`}>
            Kategorie-spezifisch
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hersteller & Seriennummer jetzt hier */}
            <div>
              <Label htmlFor={fieldId("hersteller")}>Hersteller</Label>
              <Input id={fieldId("hersteller")} aria-label="Inventar Hersteller" value={form.hersteller || ""} onChange={e => setVal("hersteller", e.target.value)} maxLength={255} disabled={isSaving} />
            </div>
            <div>
              <Label htmlFor={fieldId("seriennummer")}>Seriennummer</Label>
              <Input id={fieldId("seriennummer")} aria-label="Inventar Seriennummer" value={form.seriennummer || ""} onChange={e => setVal("seriennummer", e.target.value)} maxLength={120} disabled={isSaving} />
            </div>

            {/* dynamische Felder */}
            {dynFields.map(feld => <div key={feld}>
                <Label htmlFor={detailId(feld)}>{feld}</Label>
                <Input id={detailId(feld)} aria-label={`Inventar Detail ${feld}`} value={(form?.details || {})[feld] || ""} onChange={e => setDetail(feld, e.target.value)} disabled={isSaving} />
              </div>)}
          </div>
        </div>}

      {touched && !okBase && <div className="md:col-span-2" role="alert" aria-live="assertive">
          <p className={"text-rose-300 text-sm"}>
            Bitte alle Pflichtfelder ausfuellen: Projekttyp, Kategorie, Bezeichnung und Standorttyp.
          </p>
        </div>}

      {/* Actions */}
      <div className="md:col-span-2 hidden md:flex justify-end gap-2 pt-2">
        <AppButton type="button" variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>
          Abbrechen
        </AppButton>
        <AppButton
          type="submit"
          variant="primary"
          size="sm"
          disabled={!okBase || isSaving}
          className={!okBase || isSaving ? entityFormSubmitDisabledClassName : entityFormSubmitPrimaryClassName}
          title={!okBase ? "Bitte alle Pflichtfelder ausfüllen" : undefined}
        >
          {isSaving ? (isEdit ? "Speichert…" : "Erstellt…") : isEdit ? "Speichern" : "Erstellen"}
        </AppButton>
      </div>

      {/* Mobile Action Bar */}
      <div className="md:col-span-2 h-20 md:h-0" />
      <div className="fixed md:hidden inset-x-0 bottom-0 z-40 px-3">
        <div className={`pointer-events-auto backdrop-blur border rounded-2xl shadow-lg p-2 mb-3 transition-all ${"bg-black/40 border-white/10"}`} style={{
        paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))"
      }}>
          <div className="grid grid-cols-2 gap-2">
            <AppButton
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSaving}
              className="rounded-xl px-4 py-3 active:scale-[0.98] min-h-[44px]"
            >
              Abbrechen
            </AppButton>
            <AppButton
              type="submit"
              variant="primary"
              disabled={!okBase || isSaving}
              className={cn(
                "rounded-xl px-4 py-3 active:scale-[0.98] min-h-[44px]",
                !okBase || isSaving ? entityFormSubmitDisabledClassName : entityFormSubmitPrimaryClassName
              )}
            >
              {isSaving ? (isEdit ? "Speichert…" : "Erstellt…") : isEdit ? "Speichern" : "Erstellen"}
            </AppButton>
          </div>
        </div>
      </div>
    </form>;
}
function Label({
  children,
  ...props
}) {
    return <label {...props} className={`block mb-1 text-sm ${"text-blue-200/80"}`}>
      {children}
    </label>;
}
function Req() {
  return <span className="text-rose-300" title="Pflichtfeld">*</span>;
}
function Input(props) {
  const {
    className = "",
    disabled,
    ...rest
  } = props;
  return <FormInput {...rest} disabled={disabled} className={`w-full ${disabled ? "opacity-70 cursor-not-allowed" : ""} ${className}`} />;
}
function Textarea({
  rows = 3,
  ...props
}) {
  const {
    className = "",
    disabled,
    style,
    ...rest
  } = props;
  return <FormTextarea rows={rows} {...rest} disabled={disabled} className={`w-full resize-y ${disabled ? "opacity-70 cursor-not-allowed" : ""} ${className}`} style={{
    minHeight: `calc(${rows} * 1.5rem)`,
    ...(style || {})
  }} />;
}